import React from 'react';
import styled from 'styled-components';
import { theme } from '../theme';
import Reveal from './Reveal.jsx';
import ThreeShape from './ThreeShape.jsx';

const Section = styled.div`
  position: relative;
  padding: 70px 100px;
  border-top: 2px solid ${theme.border};
  @media (max-width: 900px) {
    padding: 50px 24px;
  }
`;
const ThreeLayer = styled.div`
  position: absolute;
  top: -40px;
  right: 60px;
  width: 380px;
  height: 380px;
  z-index: 0;
  pointer-events: none;
  opacity: 0.12;
  @media (max-width: 900px) {
    display: none;
  }
`;
const Label = styled.div`
  position: relative;
  z-index: 1;
  font-family: ${theme.fontMono};
  font-size: 13px;
  letter-spacing: 0.08em;
  color: ${theme.accent};
  text-transform: uppercase;
  margin-bottom: 30px;
`;
const Grid = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(2, minmax(0,1fr));
  gap: 36px;
  padding: 20px 10px 30px 10px;
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    padding: 10px 0 20px 0;
  }
`;
const Card = styled.div`
  position: relative;
  padding: 34px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 220px;
  transform: rotate(${(p) => p.$tilt || 0}deg);
  box-shadow: 10px 10px 0 ${(p) => p.$shadow};
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  border: ${(p) => (p.$bordered ? `1px solid ${theme.borderLight}` : 'none')};
  grid-column: ${(p) => (p.$span ? '1 / -1' : 'auto')};
  @media (max-width: 800px) {
    transform: none;
  }
`;
const Tag = styled.div`
  font-family: ${theme.fontMono};
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${(p) => p.color || 'inherit'};
`;
const Title = styled.div`
  font-family: ${theme.fontDisplay};
  font-size: 28px;
  text-transform: uppercase;
`;
const Desc = styled.div`
  font-size: 14px;
  line-height: 1.6;
  font-weight: ${(p) => (p.$bold ? 600 : 400)};
  color: ${(p) => p.color || 'inherit'};
  max-width: ${(p) => p.$wide && '640px'};
`;
const Ribbon = styled.div`
  position: absolute;
  top: 16px;
  right: -38px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  font-family: ${theme.fontMono};
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.05em;
  padding: 5px 44px;
  transform: rotate(35deg);
  animation: shimmer 2.4s ease-in-out infinite;
`;
const Link = styled.div`
  font-family: ${theme.fontMono};
  font-size: 12px;
  font-weight: 700;
  margin-top: auto;
  color: inherit;
`;

export default function Gallery() {
  return (
    <Section id="Work">
      <ThreeLayer>
        <ThreeShape color={theme.accent} shape="octahedron" speed={0.5} />
      </ThreeLayer>
      <Label>Selected Work</Label>
      <Grid>

        <Reveal>
          <Card $bg={theme.accent} $fg={theme.bg} $shadow={theme.card} $tilt={-1.4}>
            <Ribbon $bg={theme.bg} $fg={theme.accent}>SOON</Ribbon>
            <Tag>Mobile &middot; Launching soon</Tag>
            <Title>CannaLog</Title>
            <Desc $bold>Production-grade grow-journaling app for Android &amp; iOS, launching publicly soon &mdash; a rework of an earlier proof of concept, built with React Native/Expo, Node/Express, Redux Toolkit and Redis.</Desc>
            <a href="https://cannalog.co.za" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
              <Link>cannalog.co.za &#8599;</Link>
            </a>
          </Card>
        </Reveal>

        <Reveal delay="0.12s">
          <Card $bg={theme.card} $fg={theme.text} $shadow={theme.border} $tilt={1.2} $bordered>
            <Ribbon $bg={theme.green} $fg={theme.bg}>LIVE</Ribbon>
            <Tag color={theme.accent}>Marketing</Tag>
            <Title>Exclusive Pools</Title>
            <Desc color={theme.textMuted}>Marketing site for a pool-building business &mdash; React, Vite, Tailwind.</Desc>
          </Card>
        </Reveal>

        <Reveal delay="0.18s">
          <Card $bg={theme.accent} $fg={theme.bg} $shadow={theme.card} $tilt={-0.8}>
            <Tag>AI / Voice &middot; Private</Tag>
            <Title>Voice Assistant</Title>
            <Desc $bold>Local, privacy-first voice assistant &mdash; wake word, on-device transcription, Claude.</Desc>
          </Card>
        </Reveal>

        <Reveal delay="0.24s">
          <Card $bg={theme.card} $fg={theme.text} $shadow={theme.border} $tilt={1.4} $bordered>
            <Tag color={theme.accent}>IoT &middot; Private</Tag>
            <Title>Grow Room</Title>
            <Desc color={theme.textMuted}>Home Assistant running irrigation, lighting and leak-detection automations for my own grow room, with flood-pump failsafes.</Desc>
          </Card>
        </Reveal>

        <Reveal delay="0.3s" style={{ gridColumn: '1 / -1' }}>
          <Card $bg={theme.accent} $fg={theme.bg} $shadow={theme.card} $tilt={-1.1} $span>
            <Tag>Dev Tools &middot; In development, private</Tag>
            <Title>Agent OS</Title>
            <Desc $bold $wide>A self-built command center for my AI agent workflows &mdash; live agent monitoring, cron jobs, a knowledge-graph &quot;second brain&quot;, and calendar in one dashboard. My voice assistant will eventually fold into it. React, Vite, Express.</Desc>
          </Card>
        </Reveal>

      </Grid>
    </Section>
  );
}
